// src/lib/gstReports.ts (MobileWeb mirror of Desktop's gstReports.ts)
// ==========================================================================
// B2C GSTR-1 + GSTR-3B Report Generator (v1)
// Differences from Desktop:
//   - File save uses real Capacitor Filesystem.writeFile on Android.
//   - Web build still falls back to browser blob download.
// Keep aggregation logic byte-for-byte identical to Desktop's gstReports.ts.
// ==========================================================================

import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";
import { format } from "date-fns";
import { calculateInvoiceSummary } from "./billingMath";

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface GstReportData {
  transactions: any[];
  startDate: Date;
  endDate: Date;
  businessName: string;
  businessAddress: string;
  businessGstin: string;
  businessStateCode: string;
  businessStateName: string;
  periodLabel: string;
  products: any[];
  // PDF branding (mirrors MyReports report header) — all optional, missing pieces are skipped.
  businessPhone?: string;
  businessEmail?: string;
  businessLogo?: HTMLImageElement | null;
  dhLogo?: HTMLImageElement | null;
}

interface BillSummary {
  orderId: string;
  invoiceNumber: string | null;
  createdAt: string;
  cancelled: boolean;
  hsnSummary: Record<string, { taxable: number; cgst: number; sgst: number }>;
  items: any[];
  netPayable: number;
  taxableTotal: number;
  cgstTotal: number;
  sgstTotal: number;
  nilRatedTotal: number;
}

interface B2CSRow {
  type: "OE";
  placeOfSupplyCode: string;
  placeOfSupplyName: string;
  taxRatePct: number;
  taxableValue: number;
  cessAmount: number;
  ecommerceGstin: string;
}

interface HSNRow {
  hsn: string;
  description: string;
  uqc: string;
  totalQuantity: number;
  taxRatePct: number;
  taxableValue: number;
  integratedTax: number;
  centralTax: number;
  stateTax: number;
  cessAmount: number;
}

interface DocumentsIssued {
  natureOfDocument: string;
  serialFrom: string;
  serialTo: string;
  totalNumber: number;
  cancelled: number;
  netIssued: number;
}

interface Gstr3bSummary {
  taxableValue: number;
  centralTax: number;
  stateTax: number;
  integratedTax: number;
  cessAmount: number;
  nilRatedExempt: number;
  nonGstOutward: number;
  outputTaxLiability: number;
}

const STATE_CODE_MAP: Record<string, string> = {
  "01": "Jammu and Kashmir", "02": "Himachal Pradesh", "03": "Punjab",
  "04": "Chandigarh", "05": "Uttarakhand", "06": "Haryana", "07": "Delhi",
  "08": "Rajasthan", "09": "Uttar Pradesh", "10": "Bihar", "11": "Sikkim",
  "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur",
  "15": "Mizoram", "16": "Tripura", "17": "Meghalaya", "18": "Assam",
  "19": "West Bengal", "20": "Jharkhand", "21": "Odisha", "22": "Chhattisgarh",
  "23": "Madhya Pradesh", "24": "Gujarat", "25": "Daman and Diu",
  "26": "Dadra and Nagar Haveli and Daman and Diu", "27": "Maharashtra",
  "28": "Andhra Pradesh (before division)", "29": "Karnataka",
  "30": "Goa", "31": "Lakshadweep", "32": "Kerala", "33": "Tamil Nadu",
  "34": "Puducherry", "35": "Andaman and Nicobar Islands",
  "36": "Telangana", "37": "Andhra Pradesh (new)", "38": "Ladakh",
  "97": "Other Territory", "99": "Centre Jurisdiction",
};

// GSTIN format: 2 digits state code + 10-char PAN (5 letters + 4 digits + 1 letter)
// + 1 digit entity code + literal "Z" + 1 alphanumeric check digit. 15 chars total.
const GSTIN_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z]\dZ[A-Z0-9]$/;

export function resolveStateFromGstin(gstin: string | undefined | null): { code: string; name: string } | null {
  if (!gstin) return null;
  const trimmed = gstin.trim().toUpperCase();
  if (!GSTIN_REGEX.test(trimmed)) return null;
  const code = trimmed.slice(0, 2);
  const name = STATE_CODE_MAP[code];
  if (!name) return null;
  return { code, name };
}

// --------------------------------------------------------------------------
// Aggregation
// --------------------------------------------------------------------------

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

function buildBillSummaries(transactions: any[], products: any[]): BillSummary[] {
  const groups = new Map<string, any[]>();
  for (const t of transactions) {
    if (t.type !== "sale") continue;
    if (!t.isGstBill) continue;
    const key = (t.fullOrderId || (typeof t.id === "string" ? t.id.replace(/_\d+$/, "") : t.id)) || "";
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(t);
  }

  const summaries: BillSummary[] = [];
  for (const orderId of Array.from(groups.keys())) {
    const group = groups.get(orderId)!;
    const cancelled = group.some((t: any) => t.cancelled);
    const summary = calculateInvoiceSummary(group, products);
    if (!summary) continue;

    let nilRatedTotal = 0;
    for (const item of summary.discountedItems) {
      const rate = Number(item.gstRate ?? 0);
      if (rate === 0) nilRatedTotal += Number(item.taxableAmount ?? item.visualTotal ?? 0);
    }

    const taxableTotal = summary.discountedItems.reduce((s: number, it: any) => s + Number(it.taxableAmount ?? 0), 0);
    const cgstTotal = summary.discountedItems.reduce((s: number, it: any) => s + Number(it.cgst ?? 0), 0);
    const sgstTotal = summary.discountedItems.reduce((s: number, it: any) => s + Number(it.sgst ?? 0), 0);

    const invoiceNumber =
      orderId.startsWith("INV-") ? orderId :
      (group[0]?.invoiceNumber ?? null);

    summaries.push({
      orderId,
      invoiceNumber,
      createdAt: group[0]?.createdAt || new Date().toISOString(),
      cancelled,
      hsnSummary: summary.hsnSummary,
      items: summary.discountedItems,
      netPayable: summary.netPayable,
      taxableTotal: r2(taxableTotal),
      cgstTotal: r2(cgstTotal),
      sgstTotal: r2(sgstTotal),
      nilRatedTotal: r2(nilRatedTotal),
    });
  }

  summaries.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  return summaries;
}

function aggregateB2CS(bills: BillSummary[], placeOfSupply: { code: string; name: string }): B2CSRow[] {
  const buckets = new Map<string, { rate: number; taxable: number }>();
  for (const bill of bills) {
    if (bill.cancelled) continue;
    for (const item of bill.items) {
      const rate = Number(item.gstRate ?? 0);
      if (rate === 0) continue;
      const key = `${placeOfSupply.code}|${rate}`;
      const existing = buckets.get(key) || { rate, taxable: 0 };
      existing.taxable += Number(item.taxableAmount ?? 0);
      buckets.set(key, existing);
    }
  }

  return Array.from(buckets.values())
    .sort((a, b) => a.rate - b.rate)
    .map<B2CSRow>((b) => ({
      type: "OE",
      placeOfSupplyCode: placeOfSupply.code,
      placeOfSupplyName: placeOfSupply.name,
      taxRatePct: b.rate,
      taxableValue: r2(b.taxable),
      cessAmount: 0,
      ecommerceGstin: "",
    }));
}

function aggregateHSNSummary(bills: BillSummary[], products: any[]): HSNRow[] {
  const buckets = new Map<
    string,
    { hsn: string; rate: number; qty: number; taxable: number; cgst: number; sgst: number; description: string; uqc: string }
  >();
  for (const bill of bills) {
    if (bill.cancelled) continue;
    for (const item of bill.items) {
      const hsn = String(item.hsn ?? "N/A");
      const rate = Number(item.gstRate ?? 0);
      const key = `${hsn}|${rate}`;
      const product = products.find((p) => p.id === item.productId);
      const description =
        (typeof item.productName === "string" && item.productName) ||
        (product?.name as string) ||
        "Item";
      const uqc = (product?.unit && String(product.unit).toUpperCase()) || "PCS";
      const existing = buckets.get(key) || {
        hsn, rate, qty: 0, taxable: 0, cgst: 0, sgst: 0, description, uqc,
      };
      existing.qty += Number(item.quantity ?? 0);
      existing.taxable += Number(item.taxableAmount ?? 0);
      existing.cgst += Number(item.cgst ?? 0);
      existing.sgst += Number(item.sgst ?? 0);
      buckets.set(key, existing);
    }
  }

  return Array.from(buckets.values())
    .sort((a, b) => (a.hsn === b.hsn ? a.rate - b.rate : a.hsn.localeCompare(b.hsn)))
    .map<HSNRow>((b) => ({
      hsn: b.hsn,
      description: b.description,
      uqc: b.uqc,
      totalQuantity: r2(b.qty),
      taxRatePct: b.rate,
      taxableValue: r2(b.taxable),
      integratedTax: 0,
      centralTax: r2(b.cgst),
      stateTax: r2(b.sgst),
      cessAmount: 0,
    }));
}

function aggregateNilRated(bills: BillSummary[]): { intraState: number; interState: number } {
  let intraState = 0;
  for (const bill of bills) {
    if (bill.cancelled) continue;
    intraState += bill.nilRatedTotal;
  }
  return { intraState: r2(intraState), interState: 0 };
}

function buildDocumentsIssued(bills: BillSummary[]): DocumentsIssued {
  const invs = bills
    .map((b) => b.invoiceNumber || b.orderId)
    .filter((n) => typeof n === "string" && n!.startsWith("INV-"))
    .map((n) => ({ raw: n!, num: parseInt(n!.replace(/^INV-/, ""), 10) }))
    .filter((x) => Number.isFinite(x.num))
    .sort((a, b) => a.num - b.num);

  const cancelledCount = bills.filter((b) => b.cancelled && (b.invoiceNumber || b.orderId).startsWith("INV-")).length;

  return {
    natureOfDocument: "Invoices for outward supply",
    serialFrom: invs[0]?.raw || "—",
    serialTo: invs[invs.length - 1]?.raw || "—",
    totalNumber: invs.length,
    cancelled: cancelledCount,
    netIssued: invs.length - cancelledCount,
  };
}

function computeGstr3bSummary(b2cs: B2CSRow[], nilRated: { intraState: number; interState: number }): Gstr3bSummary {
  let taxableValue = 0;
  let centralTax = 0;
  let stateTax = 0;
  for (const row of b2cs) {
    taxableValue += row.taxableValue;
    const half = row.taxRatePct / 2;
    centralTax += row.taxableValue * (half / 100);
    stateTax += row.taxableValue * (half / 100);
  }
  taxableValue = r2(taxableValue);
  centralTax = r2(centralTax);
  stateTax = r2(stateTax);
  const nilRatedExempt = r2(nilRated.intraState + nilRated.interState);
  return {
    taxableValue,
    centralTax,
    stateTax,
    integratedTax: 0,
    cessAmount: 0,
    nilRatedExempt,
    nonGstOutward: 0,
    outputTaxLiability: r2(centralTax + stateTax),
  };
}

function reconcile(bills: BillSummary[], b2cs: B2CSRow[], nilRated: { intraState: number; interState: number }): void {
  const lineTaxableSum = bills
    .filter((b) => !b.cancelled)
    .reduce((s, b) => s + b.taxableTotal, 0);
  const reportTaxableSum =
    b2cs.reduce((s, r) => s + r.taxableValue, 0) + nilRated.intraState + nilRated.interState;
  const drift = Math.abs(r2(lineTaxableSum) - r2(reportTaxableSum));
  if (drift > 0.05) {
    throw new Error(
      `GST report reconciliation failed: line-item taxable sum ₹${r2(lineTaxableSum)} ` +
      `≠ B2CS+Nil-Rated sum ₹${r2(reportTaxableSum)} (drift ₹${r2(drift)}). ` +
      `Refusing to emit a misleading report.`,
    );
  }
}

// --------------------------------------------------------------------------
// PUBLIC: aggregator + Excel + PDF
// --------------------------------------------------------------------------

export interface AggregatedReport {
  bills: BillSummary[];
  b2cs: B2CSRow[];
  hsn: HSNRow[];
  nilRated: { intraState: number; interState: number };
  documentsIssued: DocumentsIssued;
  gstr3b: Gstr3bSummary;
  placeOfSupply: { code: string; name: string };
}

export function aggregateGstReport(data: GstReportData): AggregatedReport {
  const place = { code: data.businessStateCode, name: data.businessStateName };
  const start = data.startDate.getTime();
  const end = data.endDate.getTime();
  const periodTxns = data.transactions.filter((t) => {
    const at = new Date(t.createdAt).getTime();
    return Number.isFinite(at) && at >= start && at <= end;
  });

  const bills = buildBillSummaries(periodTxns, data.products);
  const b2cs = aggregateB2CS(bills, place);
  const hsn = aggregateHSNSummary(bills, data.products);
  const nilRated = aggregateNilRated(bills);
  const documentsIssued = buildDocumentsIssued(bills);
  const gstr3b = computeGstr3bSummary(b2cs, nilRated);
  reconcile(bills, b2cs, nilRated);

  return { bills, b2cs, hsn, nilRated, documentsIssued, gstr3b, placeOfSupply: place };
}

const HDR = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "8B1A1A" } }, alignment: { horizontal: "center" } };
const SUBHDR = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "0F172A" } } };
const LABEL = { font: { bold: true }, fill: { fgColor: { rgb: "FDFCF7" } } };
const NUM = { numFmt: "#,##0.00" };

async function saveExcelFile(wb: XLSX.WorkBook, fileName: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const base64 = XLSX.write(wb, { bookType: "xlsx", type: "base64" });
    try {
      const perm = await Filesystem.checkPermissions();
      if (perm.publicStorage !== "granted") await Filesystem.requestPermissions();
    } catch { /* iOS doesn't need explicit permission */ }
    await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Documents });
  } else {
    XLSX.writeFile(wb, fileName);
  }
}

async function savePdfFile(doc: jsPDF, fileName: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const pdfBase64 = doc.output("dataurlstring").split(",")[1];
    try {
      const perm = await Filesystem.checkPermissions();
      if (perm.publicStorage !== "granted") await Filesystem.requestPermissions();
    } catch { /* iOS doesn't need explicit permission */ }
    await Filesystem.writeFile({ path: fileName, data: pdfBase64, directory: Directory.Documents });
  } else {
    doc.save(fileName);
  }
}

export async function generateGstReportExcel(data: GstReportData): Promise<void> {
  const agg = aggregateGstReport(data);
  const wb = XLSX.utils.book_new();

  const cover = XLSX.utils.aoa_to_sheet([
    [{ v: data.businessName.toUpperCase(), s: { font: { bold: true, sz: 18, color: { rgb: "8B1A1A" } } } }],
    [{ v: "GST FILING REPORT (B2C)", s: { font: { bold: true, sz: 14, color: { rgb: "0F172A" } } } }],
    [{ v: `Period: ${data.periodLabel}` }],
    [{ v: `Date Range: ${format(data.startDate, "dd MMM yyyy")} → ${format(data.endDate, "dd MMM yyyy")}` }],
    [{ v: `GSTIN: ${data.businessGstin}` }],
    [{ v: `Place of Supply: ${data.businessStateName} (${data.businessStateCode})` }],
    [],
    [{ v: "AT A GLANCE", s: SUBHDR }],
    [{ v: "Total Bills (incl. Cancelled)", s: LABEL }, { v: agg.documentsIssued.totalNumber, t: "n" }],
    [{ v: "Cancelled Invoices", s: LABEL }, { v: agg.documentsIssued.cancelled, t: "n" }],
    [{ v: "Net Issued", s: LABEL }, { v: agg.documentsIssued.netIssued, t: "n" }],
    [{ v: "B2CS Taxable Value", s: LABEL }, { v: agg.gstr3b.taxableValue, t: "n", s: NUM }],
    [{ v: "Total CGST", s: LABEL }, { v: agg.gstr3b.centralTax, t: "n", s: NUM }],
    [{ v: "Total SGST", s: LABEL }, { v: agg.gstr3b.stateTax, t: "n", s: NUM }],
    [{ v: "Nil-Rated / Exempted", s: LABEL }, { v: agg.gstr3b.nilRatedExempt, t: "n", s: NUM }],
    [{ v: "Output Tax Liability", s: { ...LABEL, font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "0F172A" } } } }, { v: agg.gstr3b.outputTaxLiability, t: "n", s: { ...NUM, font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "0F172A" } } } }],
    [],
    [{ v: "NOTE: v1 covers B2C intra-state outward supplies only. Refunds (Credit Notes), B2B invoices, exports, and ITC must be entered on the GST portal manually." }],
  ]);
  cover["!cols"] = [{ wch: 32 }, { wch: 22 }];
  XLSX.utils.book_append_sheet(wb, cover, "Cover");

  const b2csRows = [
    [
      { v: "Type", s: HDR }, { v: "Place Of Supply", s: HDR }, { v: "Rate", s: HDR },
      { v: "Taxable Value", s: HDR }, { v: "Cess Amount", s: HDR }, { v: "E-Commerce GSTIN", s: HDR },
    ],
    ...agg.b2cs.map((r) => [
      r.type,
      `${r.placeOfSupplyCode}-${r.placeOfSupplyName}`,
      { v: r.taxRatePct, t: "n" },
      { v: r.taxableValue, t: "n", s: NUM },
      { v: r.cessAmount, t: "n", s: NUM },
      r.ecommerceGstin,
    ]),
  ];
  const b2cs = XLSX.utils.aoa_to_sheet(b2csRows);
  b2cs["!cols"] = [{ wch: 8 }, { wch: 30 }, { wch: 10 }, { wch: 18 }, { wch: 14 }, { wch: 22 }];
  XLSX.utils.book_append_sheet(wb, b2cs, "B2CS");

  const hsnRows = [
    [
      { v: "HSN", s: HDR }, { v: "Description", s: HDR }, { v: "UQC", s: HDR },
      { v: "Total Quantity", s: HDR }, { v: "Rate", s: HDR },
      { v: "Taxable Value", s: HDR }, { v: "Integrated Tax", s: HDR },
      { v: "Central Tax", s: HDR }, { v: "State/UT Tax", s: HDR }, { v: "Cess", s: HDR },
    ],
    ...agg.hsn.map((r) => [
      r.hsn, r.description, r.uqc,
      { v: r.totalQuantity, t: "n" },
      { v: r.taxRatePct, t: "n" },
      { v: r.taxableValue, t: "n", s: NUM },
      { v: r.integratedTax, t: "n", s: NUM },
      { v: r.centralTax, t: "n", s: NUM },
      { v: r.stateTax, t: "n", s: NUM },
      { v: r.cessAmount, t: "n", s: NUM },
    ]),
  ];
  const hsnSheet = XLSX.utils.aoa_to_sheet(hsnRows);
  hsnSheet["!cols"] = [
    { wch: 12 }, { wch: 40 }, { wch: 8 }, { wch: 14 }, { wch: 8 },
    { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 },
  ];
  XLSX.utils.book_append_sheet(wb, hsnSheet, "HSN Summary");

  const nilRows = [
    [{ v: "Description", s: HDR }, { v: "Nil Rated Supplies", s: HDR }, { v: "Exempted", s: HDR }, { v: "Non-GST", s: HDR }],
    ["Inter-state supplies to unregistered persons", { v: agg.nilRated.interState, t: "n", s: NUM }, 0, 0],
    ["Intra-state supplies to unregistered persons", { v: agg.nilRated.intraState, t: "n", s: NUM }, 0, 0],
  ];
  const nilSheet = XLSX.utils.aoa_to_sheet(nilRows);
  nilSheet["!cols"] = [{ wch: 50 }, { wch: 18 }, { wch: 14 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, nilSheet, "Nil Rated");

  const di = agg.documentsIssued;
  const diRows = [
    [
      { v: "Nature of Document", s: HDR }, { v: "Sr. No. From", s: HDR }, { v: "Sr. No. To", s: HDR },
      { v: "Total Number", s: HDR }, { v: "Cancelled", s: HDR }, { v: "Net Issued", s: HDR },
    ],
    [di.natureOfDocument, di.serialFrom, di.serialTo, di.totalNumber, di.cancelled, di.netIssued],
  ];
  const diSheet = XLSX.utils.aoa_to_sheet(diRows);
  diSheet["!cols"] = [{ wch: 32 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wb, diSheet, "Documents Issued");

  const g3 = agg.gstr3b;
  const g3bRows = [
    [{ v: "Section", s: HDR }, { v: "Description", s: HDR }, { v: "Taxable Value", s: HDR }, { v: "CGST", s: HDR }, { v: "SGST", s: HDR }, { v: "IGST", s: HDR }, { v: "Cess", s: HDR }],
    ["3.1(a)", "Outward taxable supplies (other than zero-rated, nil-rated and exempted)", { v: g3.taxableValue, t: "n", s: NUM }, { v: g3.centralTax, t: "n", s: NUM }, { v: g3.stateTax, t: "n", s: NUM }, { v: g3.integratedTax, t: "n", s: NUM }, { v: g3.cessAmount, t: "n", s: NUM }],
    ["3.1(b)", "Outward taxable supplies (zero-rated)", 0, 0, 0, 0, 0],
    ["3.1(c)", "Other outward supplies (Nil-rated, exempted)", { v: g3.nilRatedExempt, t: "n", s: NUM }, 0, 0, 0, 0],
    ["3.1(d)", "Inward supplies (liable to reverse charge)", "Manual", "Manual", "Manual", "Manual", "Manual"],
    ["3.1(e)", "Non-GST outward supplies", { v: g3.nonGstOutward, t: "n", s: NUM }, 0, 0, 0, 0],
    [],
    [{ v: "Output Tax Liability (CGST + SGST + IGST + Cess)", s: SUBHDR }, "", { v: g3.outputTaxLiability, t: "n", s: { ...NUM, font: { bold: true } } }],
    [],
    [{ v: "Table 4 (ITC on purchases) — file manually on the portal." }],
  ];
  const g3Sheet = XLSX.utils.aoa_to_sheet(g3bRows);
  g3Sheet["!cols"] = [{ wch: 10 }, { wch: 60 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, g3Sheet, "GSTR-3B Summary");

  await saveExcelFile(wb, `DailyHisaab_GSTR-${data.periodLabel}.xlsx`);
}

// Branded palette — must match reports.ts so the GST PDF visually matches the
// existing Statement Center PDFs (logo placement, off-white strip, red accent line).
const BRAND_RED: [number, number, number] = [139, 26, 26];
const APP_OFF_WHITE: [number, number, number] = [253, 252, 247];
const SLATE_900: [number, number, number] = [15, 23, 42];
const SLATE_500: [number, number, number] = [100, 116, 139];

const fmtINR = (n: number) => n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Branded header (off-white strip + business logo + DailyHisaab logo). Drawn on every page. */
function drawBrandedHeader(doc: jsPDF, pageW: number, data: GstReportData): void {
  doc.setFillColor(...APP_OFF_WHITE);
  doc.rect(0, 0, pageW, 50, "F");
  doc.setDrawColor(...BRAND_RED);
  doc.setLineWidth(1.5);
  doc.line(0, 50, pageW, 50);

  let textX = 15;
  if (data.businessLogo) {
    try {
      const logoSize = 30;
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(15, 10, logoSize + 4, logoSize + 4, 3, 3, "F");
      doc.addImage(data.businessLogo, "PNG", 17, 12, logoSize, logoSize);
      textX = 15 + logoSize + 10;
    } catch (e) {
      console.error("[gstReports] failed to add business logo:", e);
    }
  }

  doc.setTextColor(...BRAND_RED);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(data.businessName.toUpperCase(), textX, 22);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...SLATE_500);
  let leftY = 30;
  if (data.businessAddress) {
    doc.text(data.businessAddress, textX, leftY);
    leftY += 4.5;
  }
  if (data.businessGstin) {
    doc.text(`GSTIN: ${data.businessGstin}`, textX, leftY);
    leftY += 4.5;
  }
  const contactInfo: string[] = [];
  if (data.businessPhone) contactInfo.push(`Tel: ${data.businessPhone}`);
  if (data.businessEmail) contactInfo.push(`Email: ${data.businessEmail}`);
  if (contactInfo.length > 0) doc.text(contactInfo.join("  |  "), textX, leftY);

  if (data.dhLogo) {
    try {
      const aspectRatio = data.dhLogo.naturalWidth / data.dhLogo.naturalHeight;
      const imgH = 8;
      const imgW = imgH * aspectRatio;
      doc.addImage(data.dhLogo, "PNG", pageW - 15 - imgW, 10, imgW, imgH);
    } catch (e) {
      console.error("[gstReports] failed to add DH logo:", e);
    }
  }

  doc.setTextColor(...SLATE_900);
}

/** Per-page footer + watermark, called after all pages drawn. */
function applyFooterAndWatermark(doc: jsPDF, pageW: number, pageH: number, dhLogo: HTMLImageElement | null | undefined): void {
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    if (dhLogo && typeof document !== "undefined") {
      try {
        const wmWidth = 80;
        const wmHeight = wmWidth / (dhLogo.naturalWidth / dhLogo.naturalHeight);
        const canvas = document.createElement("canvas");
        canvas.width = dhLogo.naturalWidth;
        canvas.height = dhLogo.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.globalAlpha = 0.04;
          ctx.drawImage(dhLogo, 0, 0);
          doc.addImage(canvas.toDataURL("image/png"), "PNG", (pageW - wmWidth) / 2, (pageH - wmHeight) / 2, wmWidth, wmHeight);
        }
      } catch {
        // Watermark is decorative; never fail the export over it.
      }
    }

    doc.setFontSize(7.5);
    doc.setTextColor(...SLATE_500);
    doc.text(
      `DailyHisaab GST Filing Report | Generated: ${format(new Date(), "dd-MM-yyyy HH:mm")}`,
      15,
      pageH - 10,
    );
    doc.text(`Page ${i} of ${pageCount}`, pageW - 30, pageH - 10);
  }
}

export async function generateGstReportPdf(data: GstReportData): Promise<void> {
  const agg = aggregateGstReport(data);
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const Y_CONTENT_START = 60;

  // ── Cover page ───────────────────────────────────────────────────────
  drawBrandedHeader(doc, pageW, data);
  let y = Y_CONTENT_START;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...SLATE_900);
  doc.text("GST FILING REPORT", 12, y);
  y += 7;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE_500);
  doc.text(`Period: ${data.periodLabel}  ·  ${format(data.startDate, "dd MMM yyyy")} → ${format(data.endDate, "dd MMM yyyy")}`, 12, y);
  y += 5;
  doc.text(`Place of Supply: ${data.businessStateName} (${data.businessStateCode})`, 12, y);
  y += 10;
  doc.setTextColor(...SLATE_900);

  autoTable(doc, {
    startY: y,
    head: [["At a Glance", "Value"]],
    body: [
      ["Total Bills (incl. Cancelled)", String(agg.documentsIssued.totalNumber)],
      ["Cancelled Invoices", String(agg.documentsIssued.cancelled)],
      ["Net Issued", String(agg.documentsIssued.netIssued)],
      ["B2CS Taxable Value", `Rs. ${fmtINR(agg.gstr3b.taxableValue)}`],
      ["Total CGST", `Rs. ${fmtINR(agg.gstr3b.centralTax)}`],
      ["Total SGST", `Rs. ${fmtINR(agg.gstr3b.stateTax)}`],
      ["Nil-Rated / Exempted", `Rs. ${fmtINR(agg.gstr3b.nilRatedExempt)}`],
      ["Output Tax Liability", `Rs. ${fmtINR(agg.gstr3b.outputTaxLiability)}`],
    ],
    theme: "grid",
    headStyles: { fillColor: SLATE_900 as any, textColor: [255, 255, 255], halign: "left" },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: 12, right: 12 },
  });

  // ── B2CS ─────────────────────────────────────────────────────────────
  doc.addPage();
  drawBrandedHeader(doc, pageW, data);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...BRAND_RED);
  doc.text("B2CS — B2C Small (intra-state)", 12, Y_CONTENT_START);
  doc.setTextColor(...SLATE_900);
  autoTable(doc, {
    startY: Y_CONTENT_START + 6,
    head: [["Type", "Place Of Supply", "Rate %", "Taxable Value", "Cess"]],
    body: agg.b2cs.length > 0 ? agg.b2cs.map((r) => [
      r.type,
      `${r.placeOfSupplyCode}-${r.placeOfSupplyName}`,
      r.taxRatePct.toString(),
      `Rs. ${fmtINR(r.taxableValue)}`,
      `Rs. ${fmtINR(r.cessAmount)}`,
    ]) : [["—", "—", "—", "—", "—"]],
    theme: "striped",
    headStyles: { fillColor: BRAND_RED as any, textColor: [255, 255, 255] },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: 12, right: 12 },
  });

  // ── HSN Summary ──────────────────────────────────────────────────────
  doc.addPage();
  drawBrandedHeader(doc, pageW, data);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...BRAND_RED);
  doc.text("HSN Summary", 12, Y_CONTENT_START);
  doc.setTextColor(...SLATE_900);
  autoTable(doc, {
    startY: Y_CONTENT_START + 6,
    head: [["HSN", "Description", "UQC", "Qty", "Rate %", "Taxable", "CGST", "SGST"]],
    body: agg.hsn.length > 0 ? agg.hsn.map((r) => [
      r.hsn,
      r.description.length > 28 ? r.description.slice(0, 28) + "…" : r.description,
      r.uqc,
      r.totalQuantity.toString(),
      r.taxRatePct.toString(),
      fmtINR(r.taxableValue),
      fmtINR(r.centralTax),
      fmtINR(r.stateTax),
    ]) : [["—", "—", "—", "—", "—", "—", "—", "—"]],
    theme: "striped",
    headStyles: { fillColor: BRAND_RED as any, textColor: [255, 255, 255] },
    styles: { fontSize: 8, cellPadding: 2.5 },
    margin: { left: 12, right: 12 },
  });

  // ── Nil-Rated + Documents Issued ─────────────────────────────────────
  doc.addPage();
  drawBrandedHeader(doc, pageW, data);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...BRAND_RED);
  doc.text("Nil-Rated / Exempted", 12, Y_CONTENT_START);
  doc.setTextColor(...SLATE_900);
  autoTable(doc, {
    startY: Y_CONTENT_START + 6,
    head: [["Description", "Nil-Rated", "Exempted", "Non-GST"]],
    body: [
      ["Inter-state to unregistered persons", fmtINR(agg.nilRated.interState), "0.00", "0.00"],
      ["Intra-state to unregistered persons", fmtINR(agg.nilRated.intraState), "0.00", "0.00"],
    ],
    theme: "striped",
    headStyles: { fillColor: BRAND_RED as any, textColor: [255, 255, 255] },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: 12, right: 12 },
  });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...BRAND_RED);
  doc.text("Documents Issued", 12, (doc as any).lastAutoTable.finalY + 12);
  doc.setTextColor(...SLATE_900);
  autoTable(doc, {
    startY: (doc as any).lastAutoTable.finalY + 16,
    head: [["Nature of Document", "From", "To", "Total", "Cancelled", "Net Issued"]],
    body: [[
      agg.documentsIssued.natureOfDocument,
      agg.documentsIssued.serialFrom,
      agg.documentsIssued.serialTo,
      String(agg.documentsIssued.totalNumber),
      String(agg.documentsIssued.cancelled),
      String(agg.documentsIssued.netIssued),
    ]],
    theme: "grid",
    headStyles: { fillColor: BRAND_RED as any, textColor: [255, 255, 255] },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: 12, right: 12 },
  });

  // ── GSTR-3B Summary ──────────────────────────────────────────────────
  doc.addPage();
  drawBrandedHeader(doc, pageW, data);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...BRAND_RED);
  doc.text("GSTR-3B Summary", 12, Y_CONTENT_START);
  doc.setTextColor(...SLATE_900);
  const g3 = agg.gstr3b;
  autoTable(doc, {
    startY: Y_CONTENT_START + 6,
    head: [["Section", "Description", "Taxable", "CGST", "SGST", "IGST", "Cess"]],
    body: [
      ["3.1(a)", "Outward taxable (other than zero/nil/exempted)", fmtINR(g3.taxableValue), fmtINR(g3.centralTax), fmtINR(g3.stateTax), fmtINR(g3.integratedTax), fmtINR(g3.cessAmount)],
      ["3.1(b)", "Outward taxable (zero-rated)", "0.00", "0.00", "0.00", "0.00", "0.00"],
      ["3.1(c)", "Nil-rated, exempted", fmtINR(g3.nilRatedExempt), "0.00", "0.00", "0.00", "0.00"],
      ["3.1(d)", "Inward (reverse charge)", "Manual", "Manual", "Manual", "Manual", "Manual"],
      ["3.1(e)", "Non-GST outward", fmtINR(g3.nonGstOutward), "0.00", "0.00", "0.00", "0.00"],
    ],
    theme: "striped",
    headStyles: { fillColor: SLATE_900 as any, textColor: [255, 255, 255] },
    styles: { fontSize: 8, cellPadding: 2.5 },
    margin: { left: 12, right: 12 },
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_RED);
  doc.text(`Output Tax Liability: Rs. ${fmtINR(g3.outputTaxLiability)}`, 12, (doc as any).lastAutoTable.finalY + 12);
  doc.setTextColor(...SLATE_900);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Table 4 (ITC on purchases) — file manually on the portal.", 12, (doc as any).lastAutoTable.finalY + 20);

  // ── Footer + watermark on every page (must run after all pages drawn) ──
  applyFooterAndWatermark(doc, pageW, pageH, data.dhLogo);

  await savePdfFile(doc, `DailyHisaab_GSTR-${data.periodLabel}.pdf`);
}
